// Shared transport for every Governance & Compliance family client
// (APP-004 R3 UI-P2).
//
// One place owns the proxy path, the bearer header, error shaping and the
// demo-mode marker, so a family client (frameworks, evidence, exceptions,
// regulatory, audit, reporting) only declares its own routes and types. All
// calls go through the Studio Worker proxy
// (`/organizations/{orgId}/policy-ai/...`), which is the authorization and
// entitlement boundary; the browser never talks to the Policy AI backend
// directly and never holds a service credential.
import { ApiError } from "./types";

export interface GovernanceResponse<T> {
  data: T;
  /** True when the Worker answered from deterministic fixtures. */
  demoMode: boolean;
  correlationId: string | null;
}

export interface GovernanceClientOptions {
  baseUrl: string;
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

/** An artifact exactly as the backend produced it, with the hash it reported. */
export interface DownloadedArtifact {
  blob: Blob;
  filename: string;
  mediaType: string;
  contentHash: string | null;
}

export type QueryValue = string | number | boolean | undefined | null;

/** Drops absent values so callers never send `?limit=undefined`. */
export function queryString(params: Record<string, QueryValue>): string {
  const q = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    q.set(key, String(value));
  }
  const s = q.toString();
  return s ? `?${s}` : "";
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * Rethrows a backend failure with its code, message and structured details
 * intact — a denial names the entitlement or permission that is missing, and
 * rewriting it into a generic error would hide the one fact an administrator
 * needs.
 */
export function raise(status: number, data: unknown): never {
  const env = (data ?? {}) as {
    error?: { code?: string; message?: string; details?: Record<string, unknown> } | string;
  };
  if (env.error && typeof env.error === "object") {
    throw new ApiError(
      status,
      env.error.code ?? "error",
      env.error.message ?? `Request failed (${status}).`,
      env.error.details,
    );
  }
  const flat = (data ?? {}) as { error?: string; message?: string };
  throw new ApiError(status, flat.error ?? "error", flat.message ?? `Request failed (${status}).`);
}

function filenameFrom(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

export class GovernanceHttp {
  private baseUrl: string;
  private getToken: () => string | null;
  private onUnauthorized: (() => void) | undefined;

  constructor(opts: GovernanceClientOptions) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, "");
    this.getToken = opts.getToken ?? (() => null);
    this.onUnauthorized = opts.onUnauthorized;
  }

  path(orgId: string, subpath: string): string {
    return `${this.baseUrl}/organizations/${orgId}/policy-ai/${subpath.replace(/^\/+/, "")}`;
  }

  private headers(withBody: boolean): Record<string, string> {
    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (withBody) headers["Content-Type"] = "application/json";
    return headers;
  }

  async request<T>(
    method: string,
    orgId: string,
    subpath: string,
    body?: unknown,
  ): Promise<GovernanceResponse<T>> {
    const init: RequestInit = { method, headers: this.headers(body !== undefined) };
    if (body !== undefined) init.body = JSON.stringify(body);
    let res: Response;
    try {
      res = await fetch(this.path(orgId, subpath), init);
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    const data = await parse(res);
    if (!res.ok) raise(res.status, data);
    return {
      data: data as T,
      demoMode: res.headers.get("x-policy-ai-mode") === "demo",
      correlationId: res.headers.get("x-correlation-id"),
    };
  }

  get<T>(orgId: string, subpath: string) {
    return this.request<T>("GET", orgId, subpath);
  }
  post<T>(orgId: string, subpath: string, body?: unknown) {
    return this.request<T>("POST", orgId, subpath, body);
  }
  del<T>(orgId: string, subpath: string) {
    return this.request<T>("DELETE", orgId, subpath);
  }

  /**
   * Downloads an attachment as bytes. The backend sends the content hash it
   * recorded; both bytes and hash are returned unchanged so the file a user
   * saves is exactly the file the export event describes.
   */
  async download(orgId: string, subpath: string, fallbackName: string): Promise<DownloadedArtifact> {
    let res: Response;
    try {
      res = await fetch(this.path(orgId, subpath), { method: "GET", headers: this.headers(false) });
    } catch {
      throw new ApiError(0, "network_error", "Unable to reach the server. Check your connection.");
    }
    if (res.status === 401 && this.onUnauthorized) this.onUnauthorized();
    if (!res.ok) raise(res.status, await parse(res));
    const mediaType = res.headers.get("content-type") ?? "application/octet-stream";
    const bytes = await res.arrayBuffer();
    return {
      blob: new Blob([bytes], { type: mediaType }),
      filename: filenameFrom(res.headers.get("content-disposition"), fallbackName),
      mediaType,
      contentHash: res.headers.get("x-content-hash"),
    };
  }
}
